<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@yuksalish/auth'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { useI18n } from 'vue-i18n'
import ReferenceItemCreateModal from './ReferenceItemCreateModal.vue'
import type { ColorReference, ReferenceItem } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  colors: ColorReference[]
}>()

const emit = defineEmits<{
  close: []
  select: [color: ColorReference]
}>()

const auth = useAuthStore()
const { t } = useI18n()
const search = ref('')
const page = ref(1)
const createOpen = ref(false)
const pageSize = 8
/**
 * Colours created from this picker during the current session. The parent owns
 * the `colors` prop and only loads it once, so without keeping the created
 * item here a new colour would never appear in the list on the next opening.
 */
const createdColors = ref<ColorReference[]>([])
const canCreate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const palette = computed<ColorReference[]>(() => {
  const seen = new Set<number>()
  const merged: ColorReference[] = []
  // Newest creations first, so a freshly added colour is always on page 1.
  for (const color of [...createdColors.value, ...props.colors]) {
    if (seen.has(color.id)) continue
    seen.add(color.id)
    merged.push(color)
  }
  return merged
})
const filteredColors = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return palette.value
  return palette.value.filter((color) =>
    [color.name, color.code]
      .some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredColors.value.length / pageSize)))
const visibleColors = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredColors.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleColors.value.map((color) => ({
    id: color.id,
    title: color.name,
    subtitle: color.code || t('engineering.passportPicker.noCode'),
    meta: `#${color.id}`,
  })),
)

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  page.value = 1
})

function selectOption(option: SmartPickerOption): void {
  const color = visibleColors.value.find((entry) => entry.id === Number(option.id))
  if (color) emit('select', color)
}

function handleCreated(color: ReferenceItem): void {
  createOpen.value = false
  const created = color as ColorReference
  if (!createdColors.value.some((entry) => entry.id === created.id)) {
    createdColors.value = [created, ...createdColors.value]
  }
  page.value = 1
  emit('select', created)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="$t('engineering.passportPicker.colorRepository')"
    :search-label="$t('engineering.passportPicker.searchColors')"
    :search-placeholder="$t('engineering.passportPicker.nameOrCode')"
    :search-value="search"
    :loading="false"
    error-message=""
    :empty-message="$t('engineering.passportPicker.noColors')"
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="$t('engineering.passportPicker.page', { count: filteredColors.length, page, total: totalPages })"
    :close-aria-label="$t('engineering.passportPicker.closeColor')"
    :retry-label="$t('engineering.passportPicker.retry')"
    :previous-label="$t('engineering.passportPicker.previous')"
    :next-label="$t('engineering.passportPicker.next')"
    :cancel-label="$t('engineering.passportPicker.cancel')"
    @close="emit('close')"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #helper>
      <div class="picker-helper">
        <div>
          <strong>{{ $t('engineering.passportPicker.needColor') }}</strong>
          <p>{{ $t('engineering.passportPicker.createAutoSelect') }}</p>
        </div>
        <button type="button" :disabled="!canCreate" @click="createOpen = true">
          {{ $t('engineering.passportPicker.addNew') }}
        </button>
      </div>
    </template>
  </SmartPickerModal>
  <ReferenceItemCreateModal
    :open="createOpen"
    :title="$t('engineering.passportPicker.createColor')"
    type="color"
    @close="createOpen = false"
    @created="handleCreated"
  />
</template>

<style scoped>
.picker-helper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d7dfdc;
  background: #f7fbff;
}

.picker-helper strong {
  color: #173454;
  font-size: 13px;
}

.picker-helper p {
  margin: 0;
  color: #66726e;
  font-size: 12px;
  line-height: 1.4;
}

.picker-helper button {
  padding: 8px 12px;
  border: 1px solid #b9c5c1;
  color: #285b9a;
  background: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.picker-helper button:disabled {
  color: #9aa7a3;
  background: #f3f6f5;
  cursor: default;
}
</style>
